import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useIonRouter } from "@ionic/react";

import { LegendVM } from "../../pages/venue/ribbonTypes";
import { LEGEND_SECTION } from "../../pages/venue/leaderboardModel";
import { trackEvent } from "../../services/analytics/analyticsService";
import {
    LEADERBOARD_RIBBON_TAPPED,
    LeaderboardTapContext,
    legendTapProps,
} from "../../pages/venue/leaderboardAnalytics";
import "./venueRibbons.css";

const KEY = "leaderboards.Legend.";

/**
 * The Local Legend entry that closes the result page (SCORZA-739 design handoff, SPEC §3).
 *
 * This is the design's "presence rule" made concrete: every metric ribbon above it can hide,
 * but this always renders, so a game where nothing notable happened still has a way into the
 * leaderboards. That includes the not-opted-in case, which renders as the invitation.
 */

export interface LocalLegendEntryProps {
    legend: LegendVM;
    venueName?: string | null;
    venueRoute: string;
    analytics: LeaderboardTapContext;
}

export const LocalLegendEntry: FC<LocalLegendEntryProps> = ({
    legend,
    venueName,
    venueRoute,
    analytics,
}) => {
    const { t } = useTranslation();
    const router = useIonRouter();

    let title: string;
    let sub: string;

    switch (legend.tier) {
        case "crown":
            title = t(KEY + "crownTitle");
            sub = t(KEY + "crownSub", { count: legend.windowDays });
            break;
        case "holding":
            // Only rank 1 is the Local Legend, so a podium place says where you are on the
            // board and how far off the title, never that you hold it.
            title = t(KEY + "holdingTitle", { rank: legend.rank });
            sub = [
                t(KEY + "holdingSub", {
                    count: legend.matchesPlayed,
                    days: legend.windowDays,
                }),
                legend.matchesToCrown
                    ? t(KEY + "holdingToCrown", {
                          count: legend.matchesToCrown,
                      })
                    : null,
            ]
                .filter(Boolean)
                .join(" · ");
            break;
        case "new":
            title = t(KEY + "newTitle");
            sub = venueName
                ? t(KEY + "newSub", { venue: venueName })
                : t(KEY + "newSubUnnamed");
            break;
        case "chasing":
            title = t(KEY + "chasingTitle");
            sub = [
                t(KEY + "chasingSub", { count: legend.matchesPlayed }),
                legend.matchesToTop3
                    ? t(KEY + "chasingToTop3", { count: legend.matchesToTop3 })
                    : null,
            ]
                .filter(Boolean)
                .join(" · ");
            break;
        case "notOptedIn":
            title = t(KEY + "notOptedInTitle");
            sub = t(KEY + "notOptedInSub");
            break;
    }

    const isCrown = legend.tier === "crown";

    return (
        <button
            type="button"
            className={`venue-legend-entry${
                isCrown ? " venue-legend-entry-crown" : ""
            }`}
            data-testid="venue-legend-entry"
            data-tier={legend.tier}
            aria-label={`${title}. ${sub}.`}
            onClick={() => {
                trackEvent(
                    LEADERBOARD_RIBBON_TAPPED,
                    legendTapProps(legend, analytics)
                );
                router.push(
                    `${venueRoute}&section=${LEGEND_SECTION}`,
                    "forward"
                );
            }}
        >
            <span className="venue-legend-entry-icon" aria-hidden="true">
                👑
            </span>
            <div className="venue-legend-entry-body">
                <h2>{title}</h2>
                <p className="venue-legend-entry-sub">{sub}</p>
            </div>
            <span className="venue-legend-entry-arrow" aria-hidden="true">
                →
            </span>
        </button>
    );
};
